import { Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { programs } from '@/mocks/neurobaby';

const nightPoses = [
  {
    id: 1,
    name: 'Postura de la luna',
    duration: '3 minutos',
    icon: 'ri-moon-line',
    description: 'Acostá a tu bebé sobre su espalda sobre una manta suave. Sentáte a su lado, cruzá las piernas o extendelas cómodamente. Estiráte hacia un lado primero, llevando el brazo por encima de la cabeza en un arco lento, como si estuvieras dibujando una luna creciente. Volvé al centro. Repetí del otro lado. Tu bebé te observa. No hace falta que haga nada. Solo que vea tu cuerpo moviéndose con calma, como las olas de la noche.',
    science: 'El estiramiento lateral suave estimula el nervio vago a través de la expansión de la caja torácica, lo que activa el sistema parasimpático. Cuando tu bebé observa tu cuerpo en movimiento lento y controlado, su cerebro registra ese patrón como una señal de seguridad. La postura de la luna también alivia la tensión acumulada en los costados durante el día de cargar, amamantar y mecer.',
    image: 'https://readdy.ai/api/search-image?query=Digital%20art%20illustration%20in%20the%20style%20of%20premium%20editorial%20magazine%20cover%2C%20hyperrealistic%20digital%20render%20with%20soft%20painterly%20brush%20strokes%2C%20warm%20golden%20earth%20tones%2C%20soft%20textured%20lighting%2C%20a%20real%20mother%20seated%20on%20a%20soft%20mat%20beside%20her%20baby%20lying%20on%20his%20back%2C%20mother%20gently%20stretching%20her%20arm%20overhead%20in%20a%20slow%20arc%20like%20a%20crescent%20moon%2C%20dimly%20lit%20room%20with%20warm%20amber%20light%2C%20warm%20cream%20and%20terracotta%20color%20palette%2C%20peaceful%20nighttime%20atmosphere%2C%20artistic%20yet%20photorealistic%2C%20editorial%20premium%20quality%2C%20not%20a%20stock%20photo%2C%20not%20cold%20or%20clinical%2C%20warm%20neutral%20palette%2C%20intimate%20moonlight%20yoga%20moment%2C%20authentic%20motherhood%20connection%2C%20soft%20textures%20and%20linen&width=800&height=500&seq=401&orientation=landscape',
  },
  {
    id: 2,
    name: 'Masaje de pies del bebé',
    duration: '2 minutos 30 segundos',
    icon: 'ri-footprint-line',
    description: 'Sostené los piececitos de tu bebé entre tus manos. Empezá con la yema del pulgar en el centro de la planta del pie, haciendo círculos diminutos y lentos. Deslizá hacia los dedos, como si estuvieras contando cada uno con un beso invisible. En el talón, presioná suavemente y soltá. Es el masaje más breve del mundo. Y el más profundo. Tu bebé no sabe qué es un pie. Pero sabe que tus manos lo hacen seguro.',
    science: 'Los pies contienen miles de terminaciones nerviosas que se conectan con todo el cuerpo. El masaje en la planta del pie, especialmente en la zona del arco, activa los receptores de presión que envían señales de calma al sistema nervioso central. Estudios de reflexología infantil muestran que el masaje de pies reduce los niveles de cortisol en bebés y aumenta la liberación de dopamina. Un pie pequeño en tus manos es un sistema nervioso completo que está aprendiendo a sentirse a salvo.',
    image: 'https://readdy.ai/api/search-image?query=Digital%20art%20illustration%20in%20the%20style%20of%20premium%20editorial%20magazine%20cover%2C%20hyperrealistic%20digital%20render%20with%20soft%20painterly%20brush%20strokes%2C%20warm%20golden%20earth%20tones%2C%20soft%20textured%20lighting%2C%20a%20real%20mother%20gently%20massaging%20her%20baby%20tiny%20feet%20with%20both%20hands%20in%20a%20dimly%20lit%20peaceful%20room%2C%20baby%20lying%20calm%20and%20relaxed%2C%20warm%20cream%20and%20terracotta%20color%20palette%2C%20soft%20amber%20night%20light%2C%20intimate%20and%20tender%20motherhood%20scene%2C%20artistic%20yet%20photorealistic%2C%20editorial%20premium%20quality%2C%20not%20a%20stock%20photo%2C%20not%20cold%20or%20clinical%2C%20warm%20neutral%20palette%2C%20foot%20massage%20and%20infant%20relaxation%2C%20authentic%20caring%20moment%2C%20soft%20textures&width=800&height=500&seq=402&orientation=landscape',
  },
  {
    id: 3,
    name: 'Postura de la mariposa',
    duration: '3 minutos',
    icon: 'ri-flower-line',
    description: 'Sentáte con las plantas de los pies juntas, rodillas abiertas suavemente. Colocá a tu bebé en tu regazo, su espalda contra tu vientre, su cabecita bajo tu barbilla. Empezá a mecerlos juntos, de adelante hacia atrás, como una mariposa que abre y cierra las alas. Despacito. El movimiento viene de tu respiración, no de tus músculos. Tu bebé siente el vaivén de tu cuerpo y su propio cuerpo se relaja en respuesta. Cantá lo que se te ocurra. O callá. Las dos opciones son hermosas.',
    science: 'La postura de la mariposa con mecimiento combina tres estímulos calmantes: el contacto piel con piel que libera oxitocina, el movimiento rítmico que activa el sistema vestibular, y la posición fetal adaptada que recuerda al bebé la seguridad del útero. Esta triada neurobiológica es una de las más poderosas para reducir la excitación cortical y facilitar la transición hacia el sueño. Tu vientre, tu voz, tu ritmo: son el lenguaje original de la calma.',
    image: 'https://readdy.ai/api/search-image?query=Digital%20art%20illustration%20in%20the%20style%20of%20premium%20editorial%20magazine%20cover%2C%20hyperrealistic%20digital%20render%20with%20soft%20painterly%20brush%20strokes%2C%20warm%20golden%20earth%20tones%2C%20soft%20textured%20lighting%2C%20a%20real%20mother%20sitting%20in%20butterfly%20yoga%20pose%20with%20baby%20nestled%20in%20her%20lap%2C%20mother%20gently%20rocking%20forward%20and%20back%2C%20dimly%20lit%20room%20with%20warm%20golden%20evening%20light%2C%20warm%20cream%20and%20terracotta%20color%20palette%2C%20peaceful%20and%20intimate%20atmosphere%2C%20artistic%20yet%20photorealistic%2C%20editorial%20premium%20quality%2C%20not%20a%20stock%20photo%2C%20not%20cold%20or%20clinical%2C%20warm%20neutral%20palette%2C%20butterfly%20pose%20mother%20and%20baby%2C%20authentic%20tender%20connection%2C%20soft%20linen%20textures&width=800&height=500&seq=403&orientation=landscape',
  },
  {
    id: 4,
    name: 'Savasana juntos',
    duration: '3 minutos 30 segundos',
    description: 'Acostáte boca arriba sobre una manta o la cama. Colocá a tu bebé sobre tu pecho, su mejilla contra tu corazón, su cuerpo extendido sobre el tuyo. Poné una mano sobre su espalda. Cerrá los ojos. Sentí su peso. Sentí su respiración. No pienses en mañana. No pienses en lo que deberías estar haciendo. Solo existí. Savasana no es hacer nada. Es permitir que todo esté bien. Es permitir que tu cuerpo, por primera vez en el día, deje de luchar. Y el de tu bebé también.',
    science: 'Savasana en contacto piel con piel es la postura más poderosa de la neurociencia del apego. La presión del cuerpo del bebé sobre el pecho de la mamá estimula la liberación de prolactina y oxitocina, mientras que la postura supina de la mamá reduce su propia tensión muscular y baja la frecuencia cardíaca. El bebé, al escuchar el corazón de la mamá y sentir su respiración, experimenta lo que los investigadores llaman entrainment biológico: la sincronización natural de los ritmos corporales entre mamá y bebé. Es la ciencia del abrazo.',
    image: 'https://readdy.ai/api/search-image?query=Digital%20art%20illustration%20in%20the%20style%20of%20premium%20editorial%20magazine%20cover%2C%20hyperrealistic%20digital%20render%20with%20soft%20painterly%20brush%20strokes%2C%20warm%20golden%20earth%20tones%2C%20soft%20textured%20lighting%2C%20a%20real%20mother%20lying%20on%20her%20back%20on%20a%20soft%20mat%20with%20baby%20resting%20on%20her%20chest%2C%20cheek%20against%20her%20heart%2C%20mother%20eyes%20closed%20in%20peaceful%20savasana%2C%20dimly%20lit%20bedroom%20with%20warm%20amber%20light%2C%20warm%20cream%20and%20terracotta%20color%20palette%2C%20serene%20and%20intimate%20nighttime%20scene%2C%20artistic%20yet%20photorealistic%2C%20editorial%20premium%20quality%2C%20not%20a%20stock%20photo%2C%20not%20cold%20or%20clinical%2C%20warm%20neutral%20palette%2C%20final%20resting%20pose%20together%2C%20authentic%20quiet%20connection%2C%20soft%20linen%20textures%20and%20peaceful%20atmosphere&width=800&height=500&seq=404&orientation=landscape',
  },
];

export default function YogaDormirSecuenciaNocturna() {
  const [activePose, setActivePose] = useState(0);
  const [scrollProgress, setScrollProgress] = useState(0);
  const { t } = useTranslation();

  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY;
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      const progress = docHeight > 0 ? scrollTop / docHeight : 0;
      setScrollProgress(progress);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="min-h-screen bg-cream">
      <Navbar />

      {/* Reading Progress Bar */}
      <div className="fixed top-0 left-0 right-0 h-1 z-50">
        <div
          className="h-full bg-terracotta transition-all duration-150"
          style={{ width: `${scrollProgress * 100}%` }}
        />
      </div>

      {/* Hero — copiado exacto de rutina-relajacion: texto izquierda, foto derecha, sin recuadro */}
      <section className="relative pt-20 md:pt-28 pb-8 md:pb-14 px-6 md:px-10">
        <div className="w-full max-w-7xl mx-auto">
          <div className="flex flex-col lg:flex-row gap-6 lg:gap-10 items-center mb-6 md:mb-10">
            {/* Texto — izquierda */}
            <div className="w-full lg:w-1/2">
              <div className="flex flex-wrap gap-2 mb-4">
                <span className="inline-block bg-[#AAB7A3] text-white text-xs font-sans font-semibold px-3 py-1 rounded-full">
                  {t('nocturna.tag1')}
                </span>
                <span className="inline-block bg-[#4A3F35] text-white text-xs font-sans font-semibold px-3 py-1 rounded-full">
                  {t('nocturna.tag2')}
                </span>
                <span className="inline-block bg-sand text-[#3D2B1F] text-xs font-sans font-semibold px-3 py-1 rounded-full">
                  {t('nocturna.tag3')}
                </span>
              </div>
              <h1 className="font-serif text-xl md:text-2xl lg:text-3xl font-bold text-[#8B6F5C] leading-tight mb-4">
                {t('nocturna.title1')} {t('nocturna.title2')} {t('nocturna.title3')}
              </h1>
              <p className="font-sans text-sm md:text-base text-[#3D2B1F] leading-relaxed">
                Cuando el día termina, el cuerpo necesita una señal. Esta secuencia le enseña a tu bebé que la noche es segura.
              </p>
            </div>

            {/* Foto — derecha, completa, sin recuadro ni zócalo */}
            <div className="w-full lg:w-1/2 flex items-center justify-center">
              <img
                src="https://storage.helloreaddy.io/project_files/33950b86-7657-493b-8177-e5932d2de9a1/e537676f-192b-4990-8a59-48dac9525341_compressed_ChatGPT-Image-11-ago-2026-10_04_32-p.m..webp"
                alt="Mamá en secuencia nocturna de yoga"
                className="w-full max-w-[270px] md:max-w-[336px] h-auto rounded-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Introducción emocional — desde la soledad y el miedo */}
      <section className="py-10 md:py-14 px-6 md:px-10 bg-[#4A3F35]">
        <div className="w-full max-w-4xl mx-auto">
            <p className="font-sans text-lg md:text-xl text-cream leading-relaxed mb-4">
              Si llegaste al final del día sin saber si lo hiciste bien. Si te sentís sola aunque haya gente alrededor. Si el miedo a la noche no es miedo a la oscuridad, es miedo al no saber...
            </p>
            <p className="font-sans text-lg md:text-xl text-cream leading-relaxed mb-4">
              <strong>Esta secuencia es para vos.</strong>
            </p>
            <p className="font-sans text-base text-cream leading-relaxed mb-4">
              Nadie te enseñó esto. Nadie te dijo que ibas a sentirte así. Y ese desamparo que sentís a veces, cuando todo el mundo duerme y vos estás despierta preguntándote si estás haciendo todo mal, es real. Es válido. Y tiene un nombre en tu cuerpo: se llama cortisol.
            </p>
            <p className="font-sans text-base text-cream leading-relaxed mb-4">
              El cortisol es la hormona del estrés. Se dispara cuando no sabemos qué viene. Cuando no hay rutina. Cuando no hay señal de que la noche va a estar bien. Tu bebé siente tu cortisol. No porque seas una mala mamá. Sino porque los cuerpos humanos están cableados para co-regularse: cuando vos estás alerta, el de él también se pone alerta. Es biología. Es amor hecho ciencia.
            </p>
            <p className="font-sans text-base text-cream leading-relaxed mb-4">
              Pero acá está la buena noticia: el cortisol tiene un enemigo natural. Se llama melatonina, la hormona del sueño, y se produce cuando el cerebro recibe las señales correctas de luz tenue, movimiento lento, contacto cálido y predecibilidad. Esos son los ingredientes de esta secuencia. Cuando la hacés, no solo estás haciendo yoga. Estás enviando una señal química a tu cerebro y al de tu bebé: <strong>la noche es segura. Podemos descansar.</strong>
            </p>
            <p className="font-sans text-base text-cream leading-relaxed mb-6">
              Esto también tiene que ver con los ritmos circadianos, el reloj biológico interno que regula cuándo tenemos sueño y cuándo estamos despiertos. Los bebés no nacen con ritmos circadianos maduros: los aprenden a través de las señales repetidas que les damos. Esta secuencia, hecha cada noche, se convierte en una señal circadiana poderosa. Tu bebé aprende: cuando mamá hace esto, viene el sueño. Y vos aprendés: cuando hago esto, vengo a casa. A tu propio cuerpo. A tu propia calma.
            </p>
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-[#AAB7A3]/20 flex items-center justify-center flex-shrink-0">
                <i className="ri-brain-line text-[#AAB7A3] text-lg" />
              </div>
              <div>
                <p className="font-sans text-sm font-semibold text-cream">Cortisol, melatonina y ritmos circadianos</p>
                <p className="font-sans text-xs text-cream">Neurobiología del sueño infantil</p>
              </div>
            </div>
        </div>
      </section>

      {/* Frase poética antes de la secuencia */}
      <section className="py-16 md:py-24 px-6 md:px-10 bg-cream">
        <div className="w-full max-w-3xl mx-auto text-center">
          <div className="w-16 h-16 rounded-full bg-[#AAB7A3]/20 flex items-center justify-center mx-auto mb-8">
            <i className="ri-star-line text-[#AAB7A3] text-2xl" />
          </div>
          <p className="font-serif text-2xl md:text-3xl text-[#8B6F5C] italic leading-relaxed mb-8">
            "No estás sola en esta oscuridad. Hay millones de mamás en este mismo momento, mirando si su bebé respira, preguntándose si lo están haciendo bien. La respuesta siempre es sí. Porque el amor que las trajo hasta acá no se equivoca."
          </p>
          <p className="font-sans text-sm text-[#5C4A3A]">
            — El equipo de NeuroBabys
          </p>
        </div>
      </section>

      {/* Las 4 Posturas Nocturnas */}
      <section className="py-16 md:py-24 px-6 md:px-10 bg-white">
        <div className="w-full max-w-7xl mx-auto">
          <div className="text-center mb-12 md:mb-16">
            <span className="inline-block font-sans text-xs font-semibold text-terracotta uppercase tracking-[0.2em] mb-4">
            {t('nocturna.posturas_tag')}
            </span>
            <h2 className="font-serif text-3xl md:text-5xl font-bold text-[#8B6F5C] leading-tight mb-4">
              {t('nocturna.posturas_title')}
            </h2>
            <p className="font-sans text-base text-[#3D2B1F] max-w-2xl mx-auto">
              {t('nocturna.posturas_desc')}
            </p>
          </div>

          {/* Tab Navigation */}
          <div className="flex flex-wrap gap-2 mb-8 md:mb-12 justify-center">
            {nightPoses.map((pose, index) => (
              <button
                key={pose.id}
                onClick={() => setActivePose(index)}
                className={`inline-flex items-center gap-2 font-sans text-sm font-semibold px-5 py-3 rounded-full transition-all duration-300 whitespace-nowrap ${
                  activePose === index
                    ? 'bg-terracotta text-white shadow-sm'
                    : 'bg-cream text-[#3D2B1F] hover:bg-sand/50 border border-sand/40'
                }`}
              >
                <span className="w-6 h-6 flex items-center justify-center rounded-full bg-white/20 text-xs font-bold">
                  {pose.id}
                </span>
                {pose.name}
              </button>
            ))}
          </div>

          {/* Active Pose Content */}
          <div className="bg-cream rounded-3xl overflow-hidden border border-sand/40">
            <div className="p-6 md:p-10">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 flex items-center justify-center rounded-2xl bg-[#4A3F35]/15 text-[#4A3F35]">
                  <i className={`${nightPoses[activePose].icon} text-xl`} />
                </div>
                <div>
                  <span className="inline-block font-sans text-xs font-semibold text-terracotta uppercase tracking-wider">
                    {t('nocturna.postura')} {nightPoses[activePose].id} de 4 · {nightPoses[activePose].duration}
                  </span>
                  <h3 className="font-serif text-2xl md:text-3xl font-bold text-[#8B6F5C] leading-tight">
                    {nightPoses[activePose].name}
                  </h3>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
                <div className="bg-white rounded-2xl p-6">
                  <span className="inline-block font-sans text-xs font-semibold text-[#5C4A3A] uppercase tracking-wider mb-3">
                    {t('nocturna.como_hacerla')}
                  </span>
                  <p className="font-sans text-sm text-[#3D2B1F] leading-relaxed">
                    {nightPoses[activePose].description}
                  </p>
                </div>
                <div className="bg-[#4A3F35] rounded-2xl p-6">
                  <span className="inline-block font-sans text-xs font-semibold text-cream uppercase tracking-wider mb-3">
                    {t('nocturna.ciencia')}
                  </span>
                  <p className="font-sans text-sm text-cream leading-relaxed">
                    {nightPoses[activePose].science}
                  </p>
                </div>
              </div>

              {/* Navigation arrows */}
              <div className="flex items-center justify-between mt-8 pt-6 border-t border-sand/30">
                <button
                  onClick={() => setActivePose((prev) => (prev > 0 ? prev - 1 : nightPoses.length - 1))}
                  className="inline-flex items-center gap-2 font-sans text-sm font-semibold text-[#3D2B1F] hover:text-terracotta transition-colors duration-300"
                >
                  <i className="ri-arrow-left-line" />
                  {t('nocturna.postura_anterior')}
                </button>
                <span className="font-sans text-xs text-[#5C4A3A]">
                  {activePose + 1} / {nightPoses.length}
                </span>
                <button
                  onClick={() => setActivePose((prev) => (prev < nightPoses.length - 1 ? prev + 1 : 0))}
                  className="inline-flex items-center gap-2 font-sans text-sm font-semibold text-[#3D2B1F] hover:text-terracotta transition-colors duration-300"
                >
                  {t('nocturna.postura_siguiente')}
                  <i className="ri-arrow-right-line" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Cierre emocional — El Principito */}
      <section className="py-10 md:py-14 px-6 md:px-10 bg-[#4A3F35]">
        <div className="w-full max-w-5xl mx-auto">
          <div className="flex flex-col md:flex-row gap-6 md:gap-8 items-start">
            {/* Foto Mercedes — margen izquierdo */}
            <div className="w-full md:w-48 lg:w-56 flex-shrink-0">
              <img
                src="https://storage.helloreaddy.io/project_files/33950b86-7657-493b-8177-e5932d2de9a1/1c53943f-8410-45c7-91d4-affe2f12e6dd_compressed_ChatGPT-Image-10-ago-2026-12_29_01-a.m.-3.webp"
                alt="Mercedes"
                className="w-full h-auto rounded-xl"
              />
            </div>

            {/* Texto — alineado a la izquierda, compacto */}
            <div className="flex-1">
              <h2 className="font-serif text-xl md:text-2xl font-bold text-cream leading-tight mb-3">
                Lo esencial es invisible <span className="italic">a los ojos</span>
              </h2>
              <p className="font-sans text-sm md:text-base text-cream leading-relaxed mb-3">
                El amor con el que atravesaste este día no está en ningún lugar visible. No se puede medir, no se puede fotografiar. No aparece en ninguna lista de tareas, no tiene likes, no tiene comentarios.
              </p>
              <p className="font-sans text-sm md:text-base text-cream leading-relaxed mb-3">
                Pero tu bebé lo sintió en cada gesto, en cada brazada, en cada vez que te levantaste aunque no quisieras. En cada noche que pasaste despierta mirándolo respirar. En cada síntoma que googlease a las tres de la mañana. En cada vez que elegiste quedarte en casa cuando querías salir. En cada vez que lloraste en la ducha y después saliste con una sonrisa.
              </p>
              <p className="font-sans text-sm md:text-base text-cream leading-relaxed mb-3">
                <strong>Eso es lo esencial.</strong>
              </p>
              <p className="font-sans text-sm md:text-base text-cream leading-relaxed mb-4">
                Y eso, nadie te lo puede quitar. Ni el cansancio. Ni la duda. Ni la noche más larga. Lo esencial ya está hecho. Y lo hiciste vos. Con tu cuerpo, con tu alma, con tu amor invisible.
              </p>

              <div className="border-t border-[#5C4A3A]/40 pt-4">
                <p className="font-serif text-sm md:text-base text-cream italic leading-relaxed mb-2">
                  "Vos ya diste todo lo que tenías que dar. No hace falta que seas perfecta. Hace falta que seas real. Y eso, ya lo sos."
                </p>
                <p className="font-sans text-xs text-cream">
                  — El equipo de NeuroBabys
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA al Programa Completo */}
      <section className="py-16 md:py-24 px-6 md:px-10 bg-cream">
        <div className="w-full max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="font-serif text-3xl md:text-5xl font-bold text-[#8B6F5C] leading-tight mb-4">
              El acompañamiento completo
            </h2>
            <p className="font-sans text-lg text-[#3D2B1F] max-w-2xl mx-auto">
              Esto es solo una muestra de lo que podés encontrar en nuestros programas.
              Elegí el que más se adapte a vos.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {programs.map((program) => (
              <div key={program.id} className="bg-white rounded-3xl overflow-hidden border border-sand/40">
                <div className="h-44 md:h-52 overflow-hidden">
                  <img
                    src={program.image}
                    alt={program.title}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="p-6 md:p-8">
                  <div className="mb-3">
                    <h3 className="font-serif text-lg md:text-xl font-bold text-[#8B6F5C] leading-tight">
                      {program.title}
                    </h3>
                    <p className="font-sans text-xs text-[#5C4A3A] mt-1">{program.subtitle}</p>
                  </div>
                  <p className="font-sans text-sm text-[#3D2B1F] leading-relaxed mb-6">
                    {program.description}
                  </p>
                  <Link
                    to={program.id === 1 ? '/fundamentos-sueno' : program.id === 2 ? '/yoga-sueno-profundo' : '/programa-completo-neurobabys'}
                    className="inline-flex items-center font-sans text-sm font-semibold text-terracotta hover:text-[#8B6F5C] transition-colors duration-300"
                  >
                    Ver programa
                    <i className="ri-arrow-right-line ml-1" />
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}